import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    
    // Log error for debugging
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    
    // Check if it's a chunk loading error
    if (error.name === 'ChunkLoadError' || 
        (error.message && error.message.includes('Loading chunk')) ||
        (error.message && error.message.includes('Failed to fetch'))) {
      console.log('[ErrorBoundary] Chunk load error detected, clearing cache and reloading...');
      
      // Clear caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Clear storage
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.log('[ErrorBoundary] Storage clear error:', e);
      }
      
      // Reload after a short delay
      setTimeout(() => {
        window.location.reload(true);
      }, 1000);
    }
  }

  handleReload = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    // Clear storage
    try {
      localStorage.removeItem('token');
      sessionStorage.clear();
    } catch (e) {
      console.log('[ErrorBoundary] Storage clear error:', e);
    }
    
    // Hard reload
    window.location.href = window.location.origin;
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">
              The application encountered an error. This might be due to a recent update. 
              Please click the button below to reload.
            </p>
            <button
              onClick={this.handleReload}
              className="w-full bg-navy hover:bg-navy/90 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Reload Application
            </button>
            <p className="text-xs text-gray-400 mt-4">
              If the problem persists, try clearing your browser cache.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
